<template>
    <div id="modal-create-tag">
        <div class="rp-overlay"></div>
        <div class="rp-modal" style="width: 470px; max-height: 80vh;">

            <div class="rp-modal-header">
                <h3 class="rp-modal-title">{{ $t('Create tag') }}</h3>
                <button v-if="!showWarning"
                    class="rp-btn-dismiss"
                    @click="$emit('closeModal')">
                    <XIcon class="w-5 h-5" />
                </button>
            </div>

            <div class="bg-white p-5">

                <template v-if="!showWarning || (Object.keys(filteredTags).length || layout.showInput)">

                    <div class="mb-4">
                        <div class="flex flex-col gap-1">
                            
                            <div class="font-medium text-base text-gray-800 mb-4">{{ $t('Add a new tag type to the dataset') }}</div>
                            <div class="text-sm mb-2">{{ $t('Tag name') }}</div>

                            <div class="flex flex-col gap-2 overflow-y-auto max-h-80">

                                <div v-for="(tag, index) in layout.inputTagNames" :key="index" class="flex flex-col gap-1 pr-2">
                                    <input
                                        ref="tagInputs"
                                        type="text"
                                        :class="['rp-input-text', errorMessages[index] ? 'border-red-500' : '']"
                                        :placeholder="$t('Enter new tag name')"
                                        v-model="layout.inputTagNames[index]"
                                        required/>
                                    <div v-if="errorMessages[index]" class="text-xs font-medium text-red-600">
                                        {{ errorMessages[index] }}
                                    </div>
                                </div>
                            </div>

                            <button 
                                type="button"
                                class="rp-btn-action-primary mt-2"
                                @click="addNewTagInput">
                                <PlusIcon class="w-4 h-4 mr-1" />
                                {{ $t('Add another tag') }}
                            </button>

                        </div>
                    </div>

                    <div class="rp-modal-body-buttons gap-3 mt-4">
                        <button v-if="!showWarning" class="rp-btn-secondary" @click="$emit('closeModal')">
                            {{ $t('Cancel') }}
                        </button>
                        <button class="rp-btn-primary" @click="createTag()" :disabled="!enableCreateButton">
                            {{ $t('Create') }}
                        </button>
                    </div>

                </template>

                <template v-else>
                    <div>
                        <div class="font-medium text-base text-gray-800 mb-2">{{ $t('No tags found') }}</div>
                        <div class="text-sm mb-2">{{ $t('Please, create one to get started') }}</div>
                    </div>
                    <div class="flex justify-end gap-3 mt-4">
                        <button class="rp-btn-secondary" @click="layout.showInput = true">
                        {{ $t('Next') }}
                        </button>
                    </div>
                </template>

            </div>

        </div>
    </div>
</template>

<script setup>
import { onMounted, computed, ref, nextTick } from "vue";
import * as rosepetalModel from "rosepetal-model";

let $dataset = rosepetalModel.default.dataset;

const emit = defineEmits(['closeModal, tagCreated']);

const props = defineProps({
    dataset: {
        type: Object,
        required: true,
    },
    interface: {
        type: String
    }
});

const layout = ref({
    dataset:        {},
    inputTagNames:  [''],
    showInput:      false
});

const tagInputs = ref([]);

const reservedNames = computed(() => {
    const type = layout.value.dataset.type;
    if (type === 'MULTICLASS' || type === 'imageObjectDetection') {
        return ["0", "unclassified"];
    } else if (type === 'MULTILABEL') {
        return ["0", "anomaly", "ok", "normal"];
    }
    return [];
});

const checkReservedNames = (name) => {
	return reservedNames.value.includes(name);
};

const checkDuplicatedName = (name, index = -1) => {
    const tags = layout.value.dataset.tags || {};

    const existsInDataset = Object.values(tags).some(
        tag => tag.id.toLowerCase() === name.toLowerCase()
    );

    const nameLower = name.toLowerCase();
    const duplicateInInputs = layout.value.inputTagNames.some((n, i) => {
        return i !== index && n.toLowerCase() === nameLower;
    });

    return existsInDataset || duplicateInInputs;
};

const isValidTagName = (name, index) => {
  return !!name.trim() && !checkReservedNames(name) && !checkDuplicatedName(name, index);
};

const enableCreateButton = computed(() => {
    return layout.value.inputTagNames.some((name, index) => isValidTagName(name, index));
});

const errorMessages = computed(() => {
  return layout.value.inputTagNames.map((name, index) => {
    if (!name) return "";
    if (!name.trim()) return "Invalid tag name";
    if (checkReservedNames(name)) return "Tag name is reserved";
    if (checkDuplicatedName(name, index)) return "Tag already exists";
    return "";
  });
});

const filteredTags = computed(() => {
    let tags = {};
    
    if (layout.value.dataset && layout.value.dataset.tags) {
        switch (layout.value.dataset.type) {
            case 'MULTILABEL':
                tags = Object.fromEntries(
                    Object.entries(layout.value.dataset.tags)
                          .filter(([_, tag]) => !tag?.normal && tag?.id !== '0')
                );
                break;
            case 'MULTICLASS':
            case 'imageObjectDetection':
            case 'ANOMALY':
                tags = Object.fromEntries(
                    Object.entries(layout.value.dataset.tags)
                          .filter(([_, tag]) => !tag?.unclassified)
                );
                break;
            default:
                tags = {};
        }
    }

    console.log("Filtered tags:", tags);

    return tags;
});

const showWarning = computed(() => {
    return !Object.keys(filteredTags.value).length && layout.value.interface === 'editor';
});

const onKeyDown = (event) => {
    if (event.key === 'Tab') {
        event.preventDefault();
        addNewTagInput();
    }
}

const addNewTagInput = () => {
    layout.value.inputTagNames.push('');
    nextTick(() => {
        const inputs = tagInputs.value;
        inputs[inputs.length - 1]?.focus();
    });
}

const createTag = async () => { 
    const tagsToCreate = layout.value.inputTagNames.filter(tag => tag.trim());
    if (tagsToCreate.length === 0) return;

    for (const tagName of tagsToCreate) {
        const tagOpt = {
            tag:        tagName.replace(/\//g, "_"),
            name:       tagName,
            anomaly:    layout.value.dataset.type === 'MULTILABEL' ? false : undefined,
        };

        try {
            await $dataset.createTag(layout.value.dataset.id, tagOpt);
        } catch (e) {
            console.error(`Error creating tag "${tagName}":`, e);
        }
    }

    emit("tagCreated");
    layout.value.inputTagNames = [""];
}

onMounted(() => {
    window.addEventListener('keydown', onKeyDown);

    if (props.dataset) {
        layout.value.dataset = props.dataset;
    }
    if (props.interface) {
        layout.value.interface = props.interface;
    }

    nextTick(() => {
        const inputs = tagInputs.value;
        inputs[inputs.length - 1]?.focus();
    });
});
</script>